import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import DialogBase from 'components/mui/Dialog';
import CircularProgressWithBackdrop from 'components/mui/CircularProgressWithBackdrop';
const Dialog = DialogBase as React.ComponentType<any>;

import { useBeneficiaryForm, useBeneficiaryDetails } from 'domain/payments/queries';
import { firstActivePaymentProviderNameSelector } from 'domain/paySettings';

import {
  VendorHeader,
  FormContent,
  Subtitle,
  VendorInfoRow,
  VendorLabel,
  AccountsChip,
  DefaultToggleBox,
  DefaultLabelContent,
  DefaultStarIcon,
  DefaultFormControlLabel,
  FormActions,
} from './StyledComponents';
import { buildZodSchema, buildDefaultValues } from './utils';
import BeneficiaryFields from './BeneficiaryFields';
import { BENEFICIARY_SOURCE } from './types';
import type { BeneficiaryModalProps, BeneficiarySource, FormValues } from './types';

const BeneficiaryModal: React.FC<BeneficiaryModalProps> = ({
  onClose,
  onSubmit,
  vendorId,
  vendorName,
  existingAccountsCount,
  beneficiaryId,
  isEditable = false,
  isDefault = false,
  isExternal = false,
}) => {
  const { formatMessage } = useIntl();
  const providerType = useSelector(firstActivePaymentProviderNameSelector);

  const isNewBeneficiary = !beneficiaryId || isExternal;

  // When an ERP-sourced beneficiary is missing required fields, the user can opt to
  // create a brand-new (user-owned, non-synced) beneficiary based on the same data.
  const [createFromErp, setCreateFromErp] = useState(false);

  const { data, isLoading } = useBeneficiaryForm();

  // NOTE: isInitialLoading, NOT isLoading. For a NEW beneficiary the details query
  // is disabled (enabled: !!id with an empty id) for the modal's whole lifetime,
  // and in TanStack Query *v4* a disabled query stays status:'loading' => isLoading:true
  // (v5 redefined isLoading as isPending && isFetching; v4 did not). Reading isLoading
  // here would pin the modal's backdrop spinner. isInitialLoading (= isLoading &&
  // isFetching) is false while disabled and true only during the real first fetch
  // (edit case). This was an observed bug, not theoretical.
  const { data: fetchedDetails, isInitialLoading: detailsLoading } = useBeneficiaryDetails(
    beneficiaryId ?? '',
    isExternal,
  );

  const resolvedInitialValues = fetchedDetails?.details;

  const schema = useMemo(
    () => (data?.fields ? buildZodSchema(data.fields, formatMessage) : z.object({})),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isSubmitting, isValid, touchedFields, dirtyFields },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useForm<FormValues>({ mode: 'onTouched', resolver: zodResolver(schema) as any });

  const contentLoading = isLoading || detailsLoading;

  useEffect(() => {
    if (!data?.fields?.length) return;
    if (!isNewBeneficiary && detailsLoading) return;
    const effectiveIsDefault = fetchedDetails?.isDefault ?? isDefault;
    reset({
      ...buildDefaultValues(data.fields, resolvedInitialValues),
      __isDefault: effectiveIsDefault ? 'true' : 'false',
    });
    trigger();
  }, [
    data,
    resolvedInitialValues,
    isNewBeneficiary,
    detailsLoading,
    reset,
    trigger,
    fetchedDetails?.isDefault,
    isDefault,
  ]);

  const isErpIncomplete = fetchedDetails?.source === BENEFICIARY_SOURCE.erp && !isValid;
  // Show the warning (and lock the fields) until the user chooses to create a new beneficiary.
  const showIncompleteErpAlert = isErpIncomplete && !createFromErp;
  const readOnly = showIncompleteErpAlert;
  // Once the user opts in, treat the flow as a fresh beneficiary creation.
  const effectiveIsNew = isNewBeneficiary || createFromErp;

  const isSelectMode = !isNewBeneficiary && !isEditable;
  const hasDirtyFields = Object.keys(dirtyFields).length > 0;
  const hasDetailsDirty = Object.keys(dirtyFields).some((key) => key !== '__isDefault');

  const onFormSubmit = useCallback(
    async ({ __isDefault, ...fields }: FormValues) => {
      const details = { ...fields, type: providerType };
      const isDefault = __isDefault === 'true';
      const source: BeneficiarySource = hasDetailsDirty ? BENEFICIARY_SOURCE.user : fetchedDetails?.source ?? BENEFICIARY_SOURCE.user;

      let submitData: Record<string, unknown>;

      if (effectiveIsNew) {
        submitData = { details, isNewBeneficiary: true, vendorId, isDefault, source };
      } else if (isEditable) {
        submitData = { details, isDefault, source };
      } else if (hasDirtyFields) {
        submitData = { payTo: beneficiaryId, details, isDefault, shouldUpdateBeneficiary: true, source };
      } else {
        submitData = { payTo: beneficiaryId };
      }

      // Close only on success. A rejected onSubmit (e.g. duplicate-beneficiary 400)
      // keeps the modal open so the user doesn't lose input; the error toast is
      // surfaced by the mutation hook's onError.
      try {
        await onSubmit(submitData);
        onClose();
      } catch {
        // keep the modal open
      }
    },
    [effectiveIsNew, isEditable, hasDirtyFields, hasDetailsDirty, vendorId, beneficiaryId, providerType, fetchedDetails, onSubmit, onClose],
  );

  const title = effectiveIsNew
    ? formatMessage({ id: 'beneficiary.modal.add.title', defaultMessage: 'Add New Beneficiary' })
    : isEditable
      ? formatMessage({ id: 'beneficiary.modal.edit.title', defaultMessage: 'Edit Beneficiary' })
      : formatMessage({ id: 'beneficiary.modal.view.title', defaultMessage: 'Beneficiary Details' });

  const subtitle = effectiveIsNew
    ? formatMessage({ id: 'beneficiary.modal.add.subtitle', defaultMessage: 'New bank account for payment' })
    : isEditable
      ? formatMessage({ id: 'beneficiary.modal.edit.subtitle', defaultMessage: 'Update bank account details' })
      : formatMessage({ id: 'beneficiary.modal.view.subtitle', defaultMessage: 'Bank account details' });

  if (contentLoading) {
    return <CircularProgressWithBackdrop />;
  }
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      withCloseBtn
      withContent={false}
      withActions={false}
      title={title}
      draggable={false}
    >
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <FormContent>
          <Subtitle variant="body2" color="text.secondary">
            {subtitle}
          </Subtitle>

          <VendorHeader>
            <VendorInfoRow>
              <ReceiptLongIcon color="primary" fontSize="medium" />
              <Box>
                <VendorLabel variant="caption" color="text.secondary">
                  {formatMessage({ id: 'beneficiary.modal.vendorFor', defaultMessage: 'Beneficiary for' })}
                </VendorLabel>
                <Typography variant="subtitle2" fontStyle="italic" fontWeight={700}>
                  {vendorName}
                </Typography>
              </Box>
            </VendorInfoRow>
            <AccountsChip
              icon={<AccountBalanceIcon />}
              label={formatMessage(
                {
                  id: 'beneficiary.modal.existingAccounts',
                  defaultMessage: '{count} existing {count, plural, one {account} other {accounts}}',
                },
                { count: existingAccountsCount },
              )}
              size="small"
              color="primary"
              variant="outlined"
            />
          </VendorHeader>

          {showIncompleteErpAlert && (
            <Alert severity="warning">
              <AlertTitle>
                {formatMessage({
                  id: 'beneficiary.modal.erpIncomplete.title',
                  defaultMessage: 'Missing required fields',
                })}
              </AlertTitle>
              {formatMessage({
                id: 'beneficiary.modal.erpIncomplete.description',
                defaultMessage:
                  'This beneficiary comes from your ERP but is missing required fields needed to create it in DOKKA-PAY. Complete the missing fields in your ERP, or create a new beneficiary based on the existing data. Note: a beneficiary created here will not be synced with your ERP.',
              })}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                <Button
                  color="warning"
                  size="small"
                  variant="outlined"
                  onClick={() => setCreateFromErp(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {formatMessage({
                    id: 'beneficiary.modal.erpIncomplete.action',
                    defaultMessage: 'Create new from this data',
                  })}
                </Button>
              </Box>
            </Alert>
          )}

          {data?.fields && (
            <BeneficiaryFields
              fields={data.fields}
              register={register}
              control={control}
              errors={errors}
              touchedFields={touchedFields}
              isSubmitting={isSubmitting}
              readOnly={readOnly}
            />
          )}

          <DefaultToggleBox>
            <Controller
              name="__isDefault"
              control={control}
              defaultValue={isDefault ? 'true' : 'false'}
              render={({ field: f }) => (
                <DefaultFormControlLabel
                  control={
                    <Switch
                      checked={f.value === 'true'}
                      onChange={(e) => f.onChange(e.target.checked ? 'true' : 'false')}
                      disabled={isSubmitting || readOnly}
                      size="small"
                    />
                  }
                  label={
                    <DefaultLabelContent>
                      <DefaultStarIcon fontSize="small" active={f.value === 'true'} />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {formatMessage({
                            id: 'beneficiary.modal.setDefault',
                            defaultMessage: 'Set as default beneficiary for this vendor',
                          })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatMessage({
                            id: 'beneficiary.modal.setDefault.description',
                            defaultMessage:
                              'Will replace the current beneficiary default. Any new Pay request will use this beneficiary.',
                          })}
                        </Typography>
                      </Box>
                    </DefaultLabelContent>
                  }
                  labelPlacement="start"
                />
              )}
            />
          </DefaultToggleBox>
        </FormContent>

        <FormActions>
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            {formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
          </Button>
          <Button
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={!isValid || (isEditable && Object.keys(dirtyFields).length === 0)}
          >
            {effectiveIsNew
              ? formatMessage({ id: 'beneficiary.modal.submit.add', defaultMessage: 'Add Beneficiary' })
              : isEditable
                ? formatMessage({ id: 'beneficiary.modal.submit.update', defaultMessage: 'Update' })
                : isSelectMode && hasDirtyFields
                  ? formatMessage({ id: 'beneficiary.modal.submit.saveAndSelect', defaultMessage: 'Save & Select' })
                  : formatMessage({ id: 'beneficiary.modal.submit.select', defaultMessage: 'Select' })}
          </Button>
        </FormActions>
      </form>
    </Dialog>
  );
};

export default BeneficiaryModal;
